import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Loader2, Download, CheckCircle2, ShieldCheck, AlertTriangle, Clock, Send,
} from "lucide-react";

const NOTIF_TYPES = [
  { k: "individual", label: "Individual notice" },
  { k: "hhs", label: "HHS notice" },
  { k: "media", label: "Media notice" },
  { k: "clinic", label: "Clinic notice (BA→CE)" },
];

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-border last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

export default function IncidentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isApprover = ["owner", "system_admin"].includes(user?.role);
  const [inc, setInc] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [assess, setAssess] = useState({ nature_extent: "", unauthorized_person: "", acquired_viewed: "", mitigation: "", proposed_determination: "breach", notes: "" });
  const [approveNote, setApproveNote] = useState("");
  const [notif, setNotif] = useState({ type: "individual", recipients_count: 0, method: "written" });

  const load = useCallback(() => {
    api.get(`/incidents/${id}`).then((r) => setInc(r.data)).catch(() => setInc(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn) => {
    setBusy(key); setErr("");
    try { await fn(); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    setBusy("");
  };

  const submitAssessment = () => act("assess", () => api.put(`/incidents/${id}/assessment`, assess));
  const approve = (decision) => act("approve", () => api.post(`/incidents/${id}/approve`, { decision, note: approveNote }));
  const addNotif = () => act("notif", () => api.post(`/incidents/${id}/notifications`, { ...notif, recipients_count: parseInt(notif.recipients_count, 10) || 0 }));
  const close = () => act("close", () => api.post(`/incidents/${id}/close`));

  const downloadEvidence = async () => {
    setBusy("evidence");
    try {
      const r = await api.get(`/incidents/${id}/evidence`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `incident-evidence-${id}.json`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setErr("Could not export evidence."); }
    setBusy("");
  };

  if (inc === null) return <div className="h-64 bg-secondary rounded-lg animate-pulse" data-testid="incident-loading" />;
  if (inc === false) return <p className="text-sm text-muted-foreground">Incident not found.</p>;

  const d = inc.deadlines || {};

  return (
    <div className="space-y-6 max-w-4xl" data-testid="incident-detail-page">
      <button onClick={() => navigate("/incidents")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Back to incidents</button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{inc.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{inc.description}</p>
        </div>
        <button onClick={downloadEvidence} disabled={busy === "evidence"} data-testid="incident-evidence-button" className="shrink-0 inline-flex items-center gap-2 border border-border rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-secondary disabled:opacity-60">
          {busy === "evidence" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Evidence packet
        </button>
      </div>

      {err && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="incident-error">{err}</p>}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="font-heading text-lg font-semibold mb-3">Incident</h3>
          <Row label="Status" value={inc.status} />
          <Row label="Discovered" value={inc.discovered_at} />
          <Row label="Source" value={inc.source === "business_associate" ? `Business associate (${inc.ba_name})` : "Internal"} />
          <Row label="Individuals affected" value={inc.affected_individuals} />
          <Row label="Data types" value={(inc.data_types || []).join(", ") || "—"} />
          {inc.determination && <Row label="Determination" value={inc.determination === "breach" ? "BREACH" : "Not a breach"} />}
          {inc.approved_by && <Row label="Approved by" value={`${inc.approved_by} (${inc.approver_role})`} />}
        </div>

        <div className="bg-card border border-border rounded-lg p-6" data-testid="incident-deadlines">
          <div className="flex items-center gap-2 mb-3"><Clock className="w-5 h-5 text-primary" /><h3 className="font-heading text-lg font-semibold">Notification Deadlines</h3></div>
          <Row label="Individual (≤60 days)" value={d.individual_due} />
          <Row label="HHS timing" value={d.hhs_timing === "concurrent_within_60_days" ? "Within 60 days (≥500)" : "Annual (sub-500)"} />
          <Row label="HHS due" value={d.hhs_due} />
          <Row label="Media notice" value={d.media_required ? `Required by ${d.media_due}` : "Not required (<500)"} />
          <Row label="Large breach (≥500)" value={d.large_breach ? "Yes" : "No"} />
        </div>
      </div>

      {/* Risk assessment */}
      <div className="bg-card border border-border rounded-lg p-6" data-testid="incident-assessment-section">
        <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-5 h-5 text-primary" /><h3 className="font-heading text-lg font-semibold">Four-Factor Risk Assessment (164.402)</h3></div>
        {inc.risk_assessment ? (
          <div className="space-y-2 text-sm" data-testid="assessment-summary">
            <p><strong>1. Nature/extent of PHI:</strong> {inc.risk_assessment.nature_extent}</p>
            <p><strong>2. Unauthorized person:</strong> {inc.risk_assessment.unauthorized_person}</p>
            <p><strong>3. Actually acquired/viewed:</strong> {inc.risk_assessment.acquired_viewed}</p>
            <p><strong>4. Mitigation:</strong> {inc.risk_assessment.mitigation}</p>
            <p className="text-muted-foreground">Proposed: <strong>{inc.risk_assessment.proposed_determination}</strong> · by {inc.risk_assessment.assessed_by}</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="assessment-form">
            <textarea data-testid="assess-nature" value={assess.nature_extent} onChange={(e) => setAssess({ ...assess, nature_extent: e.target.value })} rows={2} placeholder="1. Nature and extent of the PHI (types, identifiers)" className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            <textarea data-testid="assess-person" value={assess.unauthorized_person} onChange={(e) => setAssess({ ...assess, unauthorized_person: e.target.value })} rows={2} placeholder="2. The unauthorized person who used/received the PHI" className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            <textarea data-testid="assess-acquired" value={assess.acquired_viewed} onChange={(e) => setAssess({ ...assess, acquired_viewed: e.target.value })} rows={2} placeholder="3. Whether the PHI was actually acquired or viewed" className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            <textarea data-testid="assess-mitigation" value={assess.mitigation} onChange={(e) => setAssess({ ...assess, mitigation: e.target.value })} rows={2} placeholder="4. Extent to which risk has been mitigated" className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            <label className="text-sm block">Proposed determination
              <select data-testid="assess-determination" value={assess.proposed_determination} onChange={(e) => setAssess({ ...assess, proposed_determination: e.target.value })} className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring">
                <option value="breach">Breach — notification required</option>
                <option value="not_a_breach">Not a breach — low probability of compromise</option>
              </select>
            </label>
            <button onClick={submitAssessment} disabled={busy === "assess" || !assess.nature_extent} data-testid="assess-submit" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
              {busy === "assess" && <Loader2 className="w-4 h-4 animate-spin" />} Submit assessment
            </button>
          </div>
        )}
      </div>

      {/* Approval (human approver) */}
      {inc.risk_assessment && !inc.determination && (
        <div className="bg-card border border-amber-200 bg-amber-50/40 rounded-lg p-6" data-testid="incident-approval-section">
          <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-5 h-5 text-amber-600" /><h3 className="font-heading text-lg font-semibold">Determination requires a named approver</h3></div>
          {isApprover ? (
            <div className="space-y-3">
              <input data-testid="approve-note" value={approveNote} onChange={(e) => setApproveNote(e.target.value)} placeholder="Approval note (optional)" className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
              <div className="flex gap-2">
                <button onClick={() => approve("breach")} disabled={busy === "approve"} data-testid="approve-breach" className="inline-flex items-center gap-2 bg-rose-600 text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-rose-700 disabled:opacity-60">Approve as BREACH</button>
                <button onClick={() => approve("not_a_breach")} disabled={busy === "approve"} data-testid="approve-not-breach" className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60">Approve as NOT a breach</button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Only a clinic owner or system admin can approve the final breach determination.</p>
          )}
        </div>
      )}

      {/* Notifications */}
      {inc.determination === "breach" && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="incident-notifications-section">
          <div className="flex items-center gap-2 mb-3"><Send className="w-5 h-5 text-primary" /><h3 className="font-heading text-lg font-semibold">Breach Notifications</h3></div>
          <div className="flex flex-wrap gap-2 mb-4">
            <select data-testid="notif-type" value={notif.type} onChange={(e) => setNotif({ ...notif, type: e.target.value })} className="rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring">
              {NOTIF_TYPES.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
            </select>
            <input data-testid="notif-count" type="number" min="0" value={notif.recipients_count} onChange={(e) => setNotif({ ...notif, recipients_count: e.target.value })} placeholder="Recipients" className="w-28 rounded-md border border-input bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            <button onClick={addNotif} disabled={busy === "notif"} data-testid="notif-add" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">Record</button>
          </div>
          <div className="divide-y divide-border" data-testid="notif-list">
            {(inc.notifications || []).length === 0 ? <p className="text-sm text-muted-foreground">No notifications recorded yet.</p> : inc.notifications.map((n, i) => (
              <div key={i} className="flex items-center justify-between py-2 text-sm">
                <span className="font-medium capitalize">{n.type} notice</span>
                <span className="text-muted-foreground">{n.recipients_count} recipients · {new Date(n.sent_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Close */}
      {!["closed", "closed_not_a_breach"].includes(inc.status) && isApprover && (
        <button onClick={close} disabled={busy === "close"} data-testid="incident-close-button" className="inline-flex items-center gap-2 border border-border rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-secondary disabled:opacity-60">
          {busy === "close" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Close incident
        </button>
      )}

      {/* Activity log */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading text-lg font-semibold mb-3">Activity Log</h3>
        <div className="space-y-2" data-testid="incident-log">
          {(inc.log || []).map((l, i) => (
            <div key={i} className="text-sm flex gap-3">
              <span className="text-muted-foreground tabular-nums shrink-0">{new Date(l.at).toLocaleString()}</span>
              <span><strong>{l.actor}</strong> — {l.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
